import React, {
  useState,
  useEffect,
  useCallback,
  Children,
  cloneElement
} from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';
import {
  isDownKey,
  isUpKey,
  isLeftKey,
  isRightKey,
  isTabKey,
  isShiftTabKey,
  isEnterKey
} from 'core/utils/keyDown';

const Container = styled.section`
  display: flex;
  flex-direction: ${({ horizontal }) => (horizontal ? 'row' : 'column')};
  align-items: center;
  justify-content: center;
  & > a,
  & > a:not(:last-child) {
    margin: ${({ horizontal }) => (horizontal ? '0 2vh' : '1.5rem 0')};
  }
`;

const onPressEnter = (childrenArray, selectedIndex, setDestination) => {
  childrenArray[selectedIndex]?.props?.onClick
    ? childrenArray[selectedIndex].props.onClick()
    : setDestination(childrenArray[selectedIndex].props?.to);
};

export default ({ children, stopKeyPress, handleKeyDown, className = '', horizontal }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [destination, setDestination] = useState('');
  const isSelected = index => selectedIndex === index;

  const childrenArray = Children.toArray(children).map((child, i) => {
    const { props } = child;
    return cloneElement(child, {
      ...props,
      selected: isSelected(i),
      onMouseOver: () => setSelectedIndex(i)
    });
  });
  const maxIndex = childrenArray.length - 1;
  const incrementIndex = useCallback(
    index => {
      let updatedIndex = selectedIndex + index;
      if (updatedIndex > maxIndex) updatedIndex = 0;
      if (updatedIndex < 0) updatedIndex = maxIndex;
      setSelectedIndex(updatedIndex);
    },
    [selectedIndex, maxIndex]
  );

  const _handleKeyDown = useCallback(
    e => {
      if (handleKeyDown) {
        const overrideDefaults = handleKeyDown(
          e,
          incrementIndex,
          setDestination
        );
        if (overrideDefaults) return;
      }
      // Use left/right keys for horizontal layout, up/down for vertical
      if (horizontal) {
        if (isLeftKey(e) || isShiftTabKey(e)) {
          incrementIndex(-1);
        } else if (isRightKey(e) || isTabKey(e)) {
          incrementIndex(1);
        } else if (isEnterKey(e)) {
          onPressEnter(childrenArray, selectedIndex, setDestination);
        }
      } else {
        if (isUpKey(e) || isShiftTabKey(e)) {
          incrementIndex(-1);
        } else if (isDownKey(e) || isTabKey(e)) {
          incrementIndex(1);
        } else if (isEnterKey(e)) {
          onPressEnter(childrenArray, selectedIndex, setDestination);
        }
      }
    },
    [selectedIndex, incrementIndex, childrenArray, handleKeyDown, horizontal]
  );
  useEffect(() => {
    if (!stopKeyPress) {
      document.addEventListener('keydown', _handleKeyDown);
    }
    return () => {
      if (!stopKeyPress) {
        document.removeEventListener('keydown', _handleKeyDown);
      }
    };
  }, [_handleKeyDown, stopKeyPress]);
  return destination ? (
    <Redirect to={destination} push />
  ) : (
    <Container className={className} horizontal={horizontal}>{childrenArray}</Container>
  );
};
